import { 
  doc, 
  getDoc, 
  setDoc, 
  getDocFromCache 
} from 'firebase/firestore';
import { db } from '../config/firebase';

const SETTINGS_COLLECTION = 'systemSettings';
const ALLOWED_FILE_TYPES_DOC = 'allowedFileTypes';
const GENERAL_SETTINGS_DOC = 'generalSettings';

// Default file types configuration
const DEFAULT_FILE_TYPES = [
  {
    extension: '.aab',
    displayName: 'Android App Bundle',
    mimeType: 'application/x-authorware-bin',
    maxSize: 200 * 1024 * 1024, // 200MB in bytes
    enabled: true
  }
];

// Default general settings
const DEFAULT_GENERAL_SETTINGS = {
  systemName: 'App Analysis System',
  maxStoragePerUser: 10, // GB
  systemVersion: '1.0.0',
  developerMode: false,
  lastUpdated: new Date()
};

// Helper function to safely get a document (handles offline state)
const safeGetDoc = async (docRef) => {
  try {
    // First try to get from cache to avoid offline errors
    try {
      const cacheSnapshot = await getDocFromCache(docRef);
      if (cacheSnapshot.exists()) {
        return cacheSnapshot;
      }
    } catch (cacheError) {
      // Cache miss is expected, continue to network fetch
    }
    
    // Try network fetch
    return await getDoc(docRef);
  } catch (error) {
    // Handle offline error gracefully
    if (error.message?.includes('offline') || error.code === 'unavailable') {
      console.warn('Firestore is offline, using default settings');
      return null;
    }
    throw error;
  }
};

// Initialize default system settings if they don't exist
export const initializeSystemSettings = async () => {
  try {
    // Initialize file types settings
    const fileTypesRef = doc(db, SETTINGS_COLLECTION, ALLOWED_FILE_TYPES_DOC);
    const fileTypesSnapshot = await safeGetDoc(fileTypesRef);
    
    if (fileTypesSnapshot && !fileTypesSnapshot.exists()) {
      // Create new document with defaults
      try {
        await setDoc(fileTypesRef, {
          fileTypes: DEFAULT_FILE_TYPES,
          lastUpdated: new Date()
        });
      } catch (writeError) {
        console.warn('Could not write file types settings (may be offline):', writeError.message);
      }
    } else if (fileTypesSnapshot && fileTypesSnapshot.exists()) {
      // Migrate: if the existing config only has .apk, update it to .aab
      const existingTypes = fileTypesSnapshot.data().fileTypes || [];
      const hasOnlyApk = existingTypes.length > 0 && 
        existingTypes.every(t => t.extension === '.apk') &&
        !existingTypes.some(t => t.extension === '.aab');
      if (hasOnlyApk) {
        try {
          await setDoc(fileTypesRef, {
            fileTypes: DEFAULT_FILE_TYPES,
            lastUpdated: new Date()
          });
          console.log('Migrated file types from .apk to .aab');
        } catch (writeError) {
          console.warn('Could not migrate file types settings:', writeError.message);
        }
      }
    }

    // Initialize general settings
    const generalSettingsRef = doc(db, SETTINGS_COLLECTION, GENERAL_SETTINGS_DOC);
    const generalSettingsSnapshot = await safeGetDoc(generalSettingsRef);
    
    // Only try to create if we got a valid response and doc doesn't exist
    if (generalSettingsSnapshot && !generalSettingsSnapshot.exists()) {
      try {
        await setDoc(generalSettingsRef, {
          ...DEFAULT_GENERAL_SETTINGS,
          lastUpdated: new Date()
        });
      } catch (writeError) {
        console.warn('Could not write general settings (may be offline):', writeError.message);
      }
    }

    return true;
  } catch (error) {
    // Return true even on error - we'll use defaults
    console.warn('Error initializing system settings (using defaults):', error.message);
    return true;
  }
};

// Get general system settings
export const getGeneralSettings = async () => {
  try {
    const settingsRef = doc(db, SETTINGS_COLLECTION, GENERAL_SETTINGS_DOC);
    const settingsSnapshot = await safeGetDoc(settingsRef);
    
    if (settingsSnapshot && settingsSnapshot.exists()) {
      return settingsSnapshot.data();
    } else {
      // Initialize settings if they don't exist and return default
      await initializeSystemSettings();
      return DEFAULT_GENERAL_SETTINGS;
    }
  } catch (error) {
    console.warn('Error getting general settings (using defaults):', error.message);
    return DEFAULT_GENERAL_SETTINGS;
  }
};

// Update general system settings
export const updateGeneralSettings = async (settings) => {
  try {
    const settingsRef = doc(db, SETTINGS_COLLECTION, GENERAL_SETTINGS_DOC);
    await setDoc(settingsRef, {
      ...settings,
      lastUpdated: new Date()
    });
    return true;
  } catch (error) {
    console.error('Error updating general settings:', error);
    throw error;
  }
};

// Get all allowed file types
export const getAllowedFileTypes = async () => {
  try {
    const settingsRef = doc(db, SETTINGS_COLLECTION, ALLOWED_FILE_TYPES_DOC);
    const settingsSnapshot = await safeGetDoc(settingsRef);
    
    if (settingsSnapshot && settingsSnapshot.exists()) {
      return settingsSnapshot.data().fileTypes || DEFAULT_FILE_TYPES;
    } else {
      // Initialize settings if they don't exist and return default
      await initializeSystemSettings();
      return DEFAULT_FILE_TYPES;
    }
  } catch (error) {
    console.warn('Error getting allowed file types (using defaults):', error.message);
    return DEFAULT_FILE_TYPES;
  }
};

// Check if a file extension is allowed
export const isFileTypeAllowed = async (fileExtension) => {
  try {
    const fileTypes = await getAllowedFileTypes();
    return fileTypes.some(type => 
      type.extension.toLowerCase() === fileExtension.toLowerCase() && 
      type.enabled === true
    );
  } catch (error) {
    console.warn('Error checking file type (allowing .aab by default):', error.message);
    // Default to allowing .aab files if there's an error
    return fileExtension.toLowerCase() === '.aab';
  }
};

// Update allowed file types
export const updateAllowedFileTypes = async (fileTypes) => {
  try {
    const settingsRef = doc(db, SETTINGS_COLLECTION, ALLOWED_FILE_TYPES_DOC);
    await setDoc(settingsRef, {
      fileTypes,
      lastUpdated: new Date()
    });
    return true;
  } catch (error) {
    console.error('Error updating allowed file types:', error);
    throw error;
  }
}; 